import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ORG_ROLE_LABELS, type OrgRole } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { OrgMember } from '@/api/types';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectMembersPanel } from '@/features/projects/ProjectMembersPanel';
import styles from './ProjectsPage.module.css';

export function TeamPage() {
  const { user } = useAuth();
  const { selectedProjectId } = useProjectContext();
  const qc = useQueryClient();
  const orgQ = useQuery({
    queryKey: ['org'],
    queryFn: () => apiFetch<{ organization: { name: string; my_role: string } }>('/api/org'),
  });
  const membersQ = useQuery({
    queryKey: ['org-members'],
    queryFn: () => apiFetch<{ members: OrgMember[] }>('/api/org/members'),
  });
  const sessionsQ = useQuery({
    queryKey: ['auth-sessions'],
    queryFn: () =>
      apiFetch<{
        sessions: {
          id: string;
          last_activity_at: string;
          expires_at: string;
          auth_level: string;
        }[];
      }>('/api/auth/sessions'),
  });

  const patchRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      apiFetch(`/api/org/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org-members'] }),
  });

  const revokeSession = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/auth/sessions/${id}`, { method: 'DELETE', body: '{}' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['auth-sessions'] }),
  });

  const isOwner = orgQ.data?.organization.my_role === 'owner';

  return (
    <div>
      <h1 className={styles.title}>Team &amp; toegang</h1>
      <StateMessage title="CRM A — organisatie">
        Leden en rollen voor {orgQ.data?.organization.name ?? '…'}. Uitnodigingen per e-mail volgen
        in een latere fase.
      </StateMessage>

      {membersQ.isLoading ? (
        <LoadingState />
      ) : membersQ.isError ? (
        <ErrorState message={(membersQ.error as Error).message} />
      ) : (
        <section className={styles.card}>
          <h2>Organisatieleden</h2>
          <ul className={styles.list}>
            {(membersQ.data?.members ?? []).map((m) => (
              <li key={m.user_id} className={styles.listItem}>
                <div>
                  <strong>{m.email}</strong>
                  {isOwner && m.user_id !== user?.id ? (
                    <select
                      className={styles.filterInput}
                      value={m.role}
                      onChange={(e) =>
                        patchRole.mutate({ userId: m.user_id, role: e.target.value as OrgRole })
                      }
                    >
                      {(Object.keys(ORG_ROLE_LABELS) as OrgRole[]).map((r) => (
                        <option key={r} value={r}>
                          {ORG_ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={styles.mutedSmall}>
                      {ORG_ROLE_LABELS[m.role as OrgRole] ?? m.role}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {selectedProjectId ? (
        <section className={styles.card}>
          <h2>Projectrollen (RBAC)</h2>
          <p className={styles.mutedSmall}>
            Per-project rechten: viewer / editor / sender / admin (legacy org-rollen worden
            genormaliseerd).
          </p>
          <ProjectMembersPanel projectId={selectedProjectId} />
        </section>
      ) : null}

      <section className={styles.card}>
        <h2>Actieve sessies</h2>
        {sessionsQ.isLoading ? (
          <LoadingState />
        ) : (
          <ul className={styles.list}>
            {(sessionsQ.data?.sessions ?? []).map((s) => (
              <li key={s.id} className={styles.listItem}>
                <div>
                  <span>{new Date(s.last_activity_at).toLocaleString('nl-NL')}</span>
                  <span className={styles.mutedSmall}> · {s.auth_level}</span>
                </div>
                <Button variant="ghost" onClick={() => revokeSession.mutate(s.id)}>
                  Afmelden
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
